import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorSlideOver, PageSectionCard, ResponsiveActions } from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import DataTableWithPagination from "../../../components/DataTable";
import { anomalyService } from "../../../services/anomalyService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import type { AnomalyDefinition } from "../../../types/anomaly";
import DefinitionEditPage from "./DefinitionEditPage";

type EditorTarget = { mode: "create" } | { mode: "edit"; id: string };

export default function DefinitionListPage() {
  const { t } = useTranslation();
  const tableRef = useRef<{ refresh: () => void } | null>(null);
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [editor, setEditor] = useState<EditorTarget | null>(null);

  const refreshTable = useCallback(() => {
    tableRef.current?.refresh();
  }, []);

  const closeEditor = useCallback(() => setEditor(null), []);

  const handleEdit = useCallback((row: AnomalyDefinition) => {
    if (row.id) setEditor({ mode: "edit", id: row.id });
  }, []);

  const handleDelete = useCallback(
    async (row: AnomalyDefinition) => {
      if (!row.id) return;
      try {
        await confirmDelete({
          itemName: row.name || row.id,
          titleKey: "common.delete",
          descriptionKey: "anomaly.deleteConfirm",
        });
      } catch (error) {
        if (isCancelled(error)) return;
        throw error;
      }
      await anomalyService.removeDefinition(row.id);
      refreshTable();
    },
    [confirmDelete, isCancelled, refreshTable]
  );

  const cols = useMemo(
    () => [
      {
        name: t("anomaly.name"),
        selector: (row: AnomalyDefinition) => row.name || "",
        sortable: true,
      },
      {
        name: t("anomaly.enabled"),
        selector: (row: AnomalyDefinition) =>
          row.enabled ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" }),
      },
      {
        name: t("anomaly.detectionMode"),
        selector: () => t("anomaly.detectionModeOpenSearchAd"),
      },
      {
        name: t("anomaly.scheduleCron"),
        selector: (row: AnomalyDefinition) => row.scheduleCron || "",
      },
    ],
    [t]
  );

  return (
    <PageListContainer>
      <PageHeader
        title={t("anomaly.title")}
        actions={
          <ResponsiveActions
            items={[
              {
                key: "create",
                label: t("anomaly.create"),
                icon: "add",
                primary: true,
                onClick: () => setEditor({ mode: "create" }),
              },
            ]}
          />
        }
      />
      <PageSectionCard title={t("anomaly.listTitle")} noPadding>
        <DataTableWithPagination
          ref={tableRef}
          cols={cols}
          fetchUrl="/anomalies/definitions"
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          searchKey="name"
          searchCollapseToIcon
        />
      </PageSectionCard>

      <EditorSlideOver
        open={Boolean(editor)}
        onClose={closeEditor}
        title={editor?.mode === "create" ? t("anomaly.create") : t("anomaly.edit")}
      >
        {editor?.mode === "create" ? (
          <DefinitionEditPage mode="create" embedded onClose={closeEditor} onSaved={refreshTable} />
        ) : editor?.mode === "edit" ? (
          <DefinitionEditPage
            mode="edit"
            embedded
            definitionId={editor.id}
            onClose={closeEditor}
            onSaved={refreshTable}
          />
        ) : null}
      </EditorSlideOver>
    </PageListContainer>
  );
}
